import styled from '@emotion/styled'
import * as React from 'react'

import { ButtonIcon } from '~ui/components/buttons/ButtonIcon/ButtonIcon'
import { cn } from '~ui/libs/classnames'
import { getSpacingValue, TSingleSpace } from '~ui/styleGuide/spaces'

type TButtonIconProps = Pick<
  PropsOf<typeof ButtonIcon>,
  'onClick' | 'bgColor' | 'icon' | 'iconColor' | 'iconContrast' | 'iconSize' | 'radius' | 'padding'
>

// TODO support element(Id) as a prop, use window only if nothing is provided

type TStyledProps = {
  /**
   * max width of the screen content
   */
  screenMaxContentWidth: string
}

type TProps = NoChildren &
  TButtonIconProps &
  TStyledProps & {
    /**
     * distance of the button icon from the bottom edge
     */
    bottom?: PropsOf<typeof ButtonIcon>['bottom']
    /**
     * to align the button with the right edge of the screen content,
     * use if screen-width is calculated in percentage
     * e.g. 5vw in case of the HeroScreen
     */
    right?: PropsOf<typeof ButtonIcon>['right']
    /**
     * additional right margin
     * used to align the button with the screen content that has a padding
     */
    marginRight?: TSingleSpace
    /**
     * If `true`, the button is visible on the screen. Default is `false`.
     */
    isActive?: boolean
  }

const propForwardOptions = {
  /**
   * max width of the screen content
   */
  shouldForwardProp: (propName: string) => propName !== `screenMaxContentWidth`,
}

const StyledButtonIcon = styled(ButtonIcon, propForwardOptions)<TStyledProps>`
  --content-width: ${({ screenMaxContentWidth }) => screenMaxContentWidth};
  --x-value: max(calc(100vw - var(--content-width)) / 2, 0px);

  margin-right: ${({ right }) =>
    right ? `max(calc(var(--x-value) * -1 + ${right}), 0px)` : undefined};

  right: calc(var(--x-value) - 50px); // 50px is for slide animation
  opacity: 0;
  visibility: hidden;

  transition: all 0.3s;

  &.active {
    right: var(--x-value);
    opacity: 1;
    visibility: visible;
  }
`

/**
 * fixed button rendered on the bottom-right of the screen
 *
 * ! works only with window scroll overflow
 */
export const FloatingButton: React.FC<TProps> = ({
  bottom,
  isActive = false,
  right,
  marginRight = `none`,
  screenMaxContentWidth,
  ...buttonIconProps
}) => {
  const bottomOffset = bottom ?? getSpacingValue(`large`)

  return (
    <StyledButtonIcon
      position={`fixed`}
      shadow={`medium`}
      right={right}
      bottom={bottomOffset}
      marginRight={getSpacingValue(marginRight)}
      className={cn({ active: isActive })}
      screenMaxContentWidth={screenMaxContentWidth}
      {...buttonIconProps}
    />
  )
}
